import { $ } from "#root/dashboard/l10n";
import { dom, icon } from "#root/lib/dom";
import { numberSpacing } from "#root/lib/formatting";
import { progressChartBox } from "./progress_chart";
const MIN_OPACITY = 0.15;
const MAX_OPACITY = 1.0;
const DIM_FACTOR = 0.3; // Multiplier for non-hovered regions

const DEFAULT_BASE_COLOR = "#19de92";
const OBLAST_NAMES = {
  "cherkasy-oblast": "Черкаська область",
  "chernihiv-oblast": "Чернігівська область",
  "chernivtsi-oblast": "Чернівецька область",
  "dnipropetrovsk-oblast": "Дніпропетровська область",
  "donetsk-oblast": "Донецька область",
  "ivano-frankivsk-oblast": "Івано-Франківська область",
  "kharkiv-oblast": "Харківська область",
  "kherson-oblast": "Херсонська область",
  "khmelnytskyi-oblast": "Хмельницька область",
  "kirovohrad-oblast": "Кіровоградська область",
  "kyiv-oblast": "Київська область",
  "luhansk-oblast": "Луганська область",
  "lviv-oblast": "Львівська область",
  "mykolaiv-oblast": "Миколаївська область",
  "odesa-oblast": "Одеська область",
  "poltava-oblast": "Полтавська область",
  "rivne-oblast": "Рівненська область",
  "sumy-oblast": "Сумська область",
  "ternopil-oblast": "Тернопільська область",
  "vinnytsia-oblast": "Вінницька область",
  "volyn-oblast": "Волинська область",
  "zakarpattia-oblast": "Закарпатська область",
  "zaporizhzhia-oblast": "Запорізька область",
  "zhytomyr-oblast": "Житомирська область",
  "kyiv-union": "Київ та Київська область",
  "kyiv": "Київ",
  "crimea-union": "АР Крим та Севастополь",
  "crimea": "АР Крим",
  "sevastopol": "Севастополь",
  "primary": "Офіс Генерального прокурора"
}; // Star marker position (percentage of SVG viewBox, roughly above Kyiv)

const PRIMARY_MARKER_POSITION = {
  x: 47,
  y: 22
};

function createStarMarker(color, opacity) {
  const ns = "http://www.w3.org/2000/svg";
  const g = document.createElementNS(ns, "g");
  g.setAttribute("id", "primary");
  g.style.cursor = "pointer";
  g.style.transition = "opacity 0.2s ease, filter 0.2s ease"; // Create a 5-pointed star path

  const star = document.createElementNS(ns, "path");
  const size = 18;
  const points = 5;
  const outerRadius = size;
  const innerRadius = size * 0.4;
  let d = "";

  for (let i = 0; i < points * 2; i++) {
    const radius = i % 2 === 0 ? outerRadius : innerRadius;
    const angle = Math.PI / points * i - Math.PI / 2;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    d += (i === 0 ? "M" : "L") + x.toFixed(2) + "," + y.toFixed(2);
  }

  d += "Z";
  star.setAttribute("d", d);
  star.setAttribute("fill", color);
  star.setAttribute("fill-opacity", opacity);
  star.style.transition = "fill-opacity 0.2s ease, fill 0.2s ease, filter 0.2s ease";
  g.appendChild(star);
  return g;
}

function calculateOpacity(value, maxValue) {
  if (maxValue === 0) return MIN_OPACITY;
  return MIN_OPACITY + value / maxValue * (MAX_OPACITY - MIN_OPACITY);
}

function createTooltip() {
  const tooltip = dom("div", {
    class: "ukraine-map-tooltip",
    style: {
      display: "none",
      position: "absolute",
      pointerEvents: "none",
      zIndex: "1000"
    },
    children: [dom("div", {
      class: "ukraine-map-tooltip-label"
    }), dom("div", {
      class: "ukraine-map-tooltip-value"
    }), dom("div", {
      class: "ukraine-map-tooltip-details"
    })]
  });
  return tooltip;
}

function positionTooltip(tooltip, event, container) {
  const containerRect = container.getBoundingClientRect();
  const padding = 12; // Calculate position relative to container

  let x = event.clientX - containerRect.left + padding;
  let y = event.clientY - containerRect.top + padding; // Set initial position to measure tooltip

  tooltip.style.left = x + "px";
  tooltip.style.top = y + "px";
  const tooltipRect = tooltip.getBoundingClientRect(); // Check right boundary (relative to container)

  if (x + tooltipRect.width > containerRect.width - padding) {
    x = event.clientX - containerRect.left - tooltipRect.width - padding;
  } // Check bottom boundary (relative to container)


  if (y + tooltipRect.height > containerRect.height - padding) {
    y = event.clientY - containerRect.top - tooltipRect.height - padding;
  } // Ensure not off-screen left or top


  x = Math.max(padding, x);
  y = Math.max(padding, y);
  tooltip.style.left = x + "px";
  tooltip.style.top = y + "px";
}

function convertToProgressChartData(data) {
  const baseColor = data.baseColor || DEFAULT_BASE_COLOR;
  const sets = data.sets || []; // Calculate max value, excluding sets with ignoreForMaxValue flag

  const maxValue = Math.max(...sets.filter(s => !s.ignoreForMaxValue).map(s => s.value), 0);
  const sortedSets = [...sets].sort((a, b) => b.value - a.value);
  return {
    sets: sortedSets.map(set => ({
      label: set.label || OBLAST_NAMES[set.id] || set.id,
      value: maxValue > 0 ? set.value / maxValue * 100 : 0,
      valueFormatted: set.valueFormatted || numberSpacing(set.value),
      color: set.color || baseColor,
      details: set.details
    }))
  };
}

export function ukraineMapChartBox(data, chartCard) {
  const baseColor = data.baseColor || DEFAULT_BASE_COLOR;
  const sets = data.sets || []; // Calculate max value, excluding sets with ignoreForMaxValue flag

  const maxValue = Math.max(...sets.filter(s => !s.ignoreForMaxValue).map(s => s.value), 0); // Create data lookup map

  const dataMap = new Map();

  for (const set of sets) {
    dataMap.set(set.id, set);
  } // State for view toggle


  let isMapView = true; // Event registry for cleanup

  let eventRegistry = [];
  const container = dom("div", {
    class: "ukraine-map-chart"
  });
  const mapContainer = dom("div", {
    class: "ukraine-map-container"
  });
  const progressContainer = dom("div", {
    class: "ukraine-map-progress-container",
    style: {
      display: "none"
    }
  }); // Create tooltip

  const tooltip = createTooltip(); // Load and configure SVG

  const svgElement = icon(require("@Resources/images/icons/ukraine.svg"));
  svgElement.classList.add("ukraine-map-svg"); // Disable pointer events on the stroke layer so fills handle hover at borders

  const strokeGroup = svgElement.querySelector("#Stroke");

  if (strokeGroup) {
    strokeGroup.style.pointerEvents = "none";
  } // Store original opacities for restore on mouseleave


  const regionOpacities = new Map(); // Map set.id to index for legend lookup

  const setIdToIdx = new Map();
  sets.forEach((set, idx) => setIdToIdx.set(set.id, idx)); // Helper to show tooltip for a set

  function showTooltipForSet(set) {
    const labelEl = tooltip.querySelector(".ukraine-map-tooltip-label");
    const valueEl = tooltip.querySelector(".ukraine-map-tooltip-value");
    const detailsEl = tooltip.querySelector(".ukraine-map-tooltip-details");
    labelEl.textContent = set.label || OBLAST_NAMES[set.id] || set.id;
    valueEl.textContent = set.valueFormatted || numberSpacing(set.value);

    if (set.details) {
      detailsEl.textContent = set.details;
      detailsEl.style.display = "block";
    } else {
      detailsEl.style.display = "none";
    }
  } // Legend interaction functions


  function setActive(setId, event) {
    const set = sets.find(s => s.id === setId);
    if (!set) return;
    const idx = setIdToIdx.get(setId); // Highlight legend

    const legend = chartCard.querySelector('.chart-legend');

    if (legend) {
      legend.classList.add('active');
      [...chartCard.querySelectorAll('.chart-legend-item')].forEach(item => item.classList.toggle('active', idx == item.dataset.idx));
    } // Highlight target region, dim all others


    sets.forEach(s => {
      const originalOpacity = regionOpacities.get(s.id);
      const dimmedOpacity = originalOpacity * DIM_FACTOR; // Handle "primary" star marker

      if (s.id === "primary") {
        const starGroup = svgElement.querySelector("#primary");

        if (starGroup) {
          const star = starGroup.querySelector("path");

          if (star) {
            if (s.id === setId) {
              star.style.fillOpacity = 1;
              star.style.filter = 'brightness(1.2)';
            } else {
              star.style.fillOpacity = dimmedOpacity;
              star.style.filter = 'none';
            }
          }
        }

        return;
      }

      const element = svgElement.querySelector(`#${s.id}`);

      if (element) {
        const paths = element.tagName === 'path' ? [element] : element.querySelectorAll('path');

        if (s.id === setId) {
          // Highlight this region
          paths.forEach(path => {
            path.style.fillOpacity = 1;
            path.style.filter = 'brightness(1.2)';
          });
        } else {
          // Dim other regions proportionally
          paths.forEach(path => {
            path.style.fillOpacity = dimmedOpacity;
            path.style.filter = 'none';
          });
        }
      }
    }); // Show tooltip

    showTooltipForSet(set);
    tooltip.style.display = 'block'; // Position tooltip if event provided (from map hover)

    if (event) {
      positionTooltip(tooltip, event, mapContainer);
    } else {
      // Position tooltip near the region center (for legend hover)
      const element = svgElement.querySelector(`#${setId}`);

      if (element) {
        const rect = element.getBoundingClientRect();
        const fakeEvent = {
          clientX: rect.left + rect.width / 2,
          clientY: rect.top + rect.height / 2
        };
        positionTooltip(tooltip, fakeEvent, mapContainer);
      }
    } // Set chart active state and hover color


    chartCard.classList.add('chart-active');
    chartCard.style.setProperty('--chart-hover-color', set.color || baseColor);
  }

  function blur() {
    // Clear legend highlight
    const legend = chartCard.querySelector('.chart-legend');

    if (legend) {
      legend.classList.remove('active');
      [...chartCard.querySelectorAll('.chart-legend-item')].forEach(item => item.classList.remove('active'));
    } // Restore all map regions to original opacity


    sets.forEach(set => {
      // Handle "primary" star marker
      if (set.id === "primary") {
        const starGroup = svgElement.querySelector("#primary");

        if (starGroup) {
          const star = starGroup.querySelector("path");

          if (star) {
            const originalOpacity = regionOpacities.get(set.id);
            star.style.fillOpacity = originalOpacity;
            star.style.filter = 'none';
          }
        }

        return;
      }

      const element = svgElement.querySelector(`#${set.id}`);

      if (element) {
        const originalOpacity = regionOpacities.get(set.id);
        const paths = element.tagName === 'path' ? [element] : element.querySelectorAll('path');
        paths.forEach(path => {
          path.style.fillOpacity = originalOpacity;
          path.style.filter = 'none';
        });
      }
    }); // Clear chart active state

    chartCard.classList.remove('chart-active');
    chartCard.style.setProperty('--chart-hover-color', ''); // Hide tooltip

    tooltip.style.display = 'none';
  } // Apply colors and opacity to each region


  for (const set of sets) {
    // Handle special "primary" entity - create star marker
    if (set.id === "primary") {
      const color = set.color || baseColor;
      const opacity = set.opacity !== undefined ? set.opacity : calculateOpacity(set.value, maxValue);
      regionOpacities.set(set.id, opacity); // Get SVG viewBox to calculate position

      const viewBox = svgElement.getAttribute("viewBox");
      const [,, vbWidth, vbHeight] = viewBox ? viewBox.split(" ").map(Number) : [0, 0, 100, 100];
      const starGroup = createStarMarker(color, opacity);
      const starX = PRIMARY_MARKER_POSITION.x / 100 * vbWidth;
      const starY = PRIMARY_MARKER_POSITION.y / 100 * vbHeight;
      starGroup.setAttribute("transform", `translate(${starX}, ${starY})`);
      svgElement.appendChild(starGroup); // Add hover handlers for the star

      starGroup.addEventListener("mouseenter", e => setActive(set.id, e));
      starGroup.addEventListener("mousemove", e => positionTooltip(tooltip, e, mapContainer));
      starGroup.addEventListener("mouseleave", () => blur());
      continue;
    }

    const element = svgElement.querySelector(`#${set.id}`);

    if (!element) {
      console.warn(`Ukraine map: element with id "${set.id}" not found`);
      continue;
    }

    const color = set.color || baseColor;
    const opacity = set.opacity !== undefined ? set.opacity : calculateOpacity(set.value, maxValue);
    regionOpacities.set(set.id, opacity); // Apply styles to all paths within the element

    const paths = element.tagName === "path" ? [element] : element.querySelectorAll("path");
    paths.forEach(path => {
      path.style.fill = color;
      path.style.fillOpacity = opacity;
      path.style.transition = "fill-opacity 0.2s ease, fill 0.2s ease, filter 0.2s ease"; // Add stroke to extend hover area into border region

      path.style.stroke = color;
      path.style.strokeWidth = "2px";
      path.style.strokeOpacity = 0.01;
    }); // Hover handlers on the parent group

    element.style.cursor = "pointer";
    element.addEventListener("mouseenter", e => setActive(set.id, e));
    element.addEventListener("mousemove", e => positionTooltip(tooltip, e, mapContainer));
    element.addEventListener("mouseleave", () => blur());
  }

  mapContainer.appendChild(svgElement);
  mapContainer.appendChild(tooltip); // Build progress chart view

  const progressData = convertToProgressChartData(data);
  progressContainer.appendChild(progressChartBox(progressData));
  container.appendChild(mapContainer);
  container.appendChild(progressContainer); // Setup legend interaction after legend is created (in next frame)

  requestAnimationFrame(() => {
    [...chartCard.querySelectorAll('.chart-legend-item')].forEach(item => {
      const set = sets[item.dataset.idx];
      if (!set) return;

      const onEnter = () => setActive(set.id);

      const onLeave = () => blur();

      item.addEventListener('mouseenter', onEnter, {
        passive: true
      });
      item.addEventListener('mouseleave', onLeave, {
        passive: true
      });
      eventRegistry.push(() => {
        item.removeEventListener('mouseenter', onEnter);
        item.removeEventListener('mouseleave', onLeave);
      });
    });
  }); // Return object with toggle functionality

  container.toggleView = function () {
    isMapView = !isMapView;

    if (isMapView) {
      mapContainer.style.display = "block";
      progressContainer.style.display = "none"; // Show legend in map view

      const legendWrapper = chartCard.querySelector(".chart-legend-wrapper");
      if (legendWrapper) legendWrapper.style.display = "";
    } else {
      mapContainer.style.display = "none";
      progressContainer.style.display = "block"; // Hide legend in list view (progress chart has its own labels)

      const legendWrapper = chartCard.querySelector(".chart-legend-wrapper");
      if (legendWrapper) legendWrapper.style.display = "none";
    }
  };

  container.isMapView = () => isMapView;

  container.destroy = () => {
    eventRegistry.forEach(fn => fn());
  };

  return container;
}
export function createViewToggleButton(mapBox, chartCard) {
  const button = dom("button", {
    class: "chart-view-toggle-button",
    title: $("switch_to_list_view"),
    children: [icon(require("@Resources/images/icons/list.svg"))],
    onclick: function () {
      mapBox.toggleView();
      const isMap = mapBox.isMapView();
      this.title = isMap ? $("switch_to_list_view") : $("switch_to_map_view"); // Swap icon

      this.innerHTML = "";

      if (isMap) {
        this.appendChild(icon(require("@Resources/images/icons/list.svg")));
      } else {
        this.appendChild(icon(require("@Resources/images/icons/map.svg"))); // Make the ukraine icon smaller for button use

        const svg = this.querySelector("svg");

        if (svg) {
          svg.style.width = "16px";
          svg.style.height = "16px";
        }
      }
    }
  });
  return button;
}
export { OBLAST_NAMES };